import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ALLERGENS } from "../lib/allergens";
import { toast } from "sonner";
import { UserRound, Plus, Trash2, Pencil, X, Save, Loader2, Search, Users } from "lucide-react";

const DIETS = ["Nessuno", "Vegetariano", "Vegano", "Pescetariano", "Senza glutine", "Senza lattosio", "Halal", "Kosher", "Altro"];
const EMPTY = { first_name: "", last_name: "", diet: "", allergen_ids: [], photo_url: "", severe_allergy: false, vegan: false, gluten_free: false, vip: false, child: false, archived: false, aversions: "", intolerances: "", preferences: "", notes: "", charter_ids: [] };
const CHIPS = [
  { key: "severe_allergy", label: "Con allergie gravi" },
  { key: "vegan", label: "Vegani" },
  { key: "child", label: "Bambini" },
  { key: "vip", label: "VIP" },
  { key: "archived", label: "Archiviati" },
];
const AVATAR_COLORS = ["#0c1e3a", "#1E3A5F", "#2E5E4E", "#8A6D1F", "#3B5BA9", "#B08D57", "#7A2E2E"];

const today = () => new Date().toISOString().slice(0, 10);
const initials = (g) => ((g.first_name?.[0] || "") + (g.last_name?.[0] || "")).toUpperCase() || (g.name?.[0] || "?").toUpperCase();
const colorFor = (id) => AVATAR_COLORS[(id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const allergenName = (id) => ALLERGENS.find((a) => a.id === id)?.name || id;

export default function Ospiti() {
  const { user } = useAuth();
  const canWrite = user?.role !== "armatore";
  const [items, setItems] = useState([]);
  const [charters, setCharters] = useState([]);
  const [tab, setTab] = useState("global");
  const [q, setQ] = useState("");
  const [chips, setChips] = useState([]);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [g, c] = await Promise.all([api.get("/guests"), api.get("/charters").catch(() => ({ data: [] }))]);
      setItems(g.data); setCharters(c.data || []);
    } catch { toast.error("Caricamento non riuscito"); }
  };
  useEffect(() => { load(); }, []);

  const activeCharterIds = useMemo(
    () => charters.filter((c) => (c.start_date || "") <= today() && today() <= (c.end_date || "")).map((c) => c.id),
    [charters]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));
  const toggleArr = (k, val) => setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));
  const toggleChip = (k) => setChips((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));

  const handlePhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 256;
        const scale = Math.min(max / img.width, max / img.height, 1);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        set("photo_url", canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const openNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (g) => { setForm({ ...EMPTY, ...g }); setEditingId(g.id); setDetail(null); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error("Inserisci il nome dell'ospite"); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/guests/${editingId}`, form); toast.success("Ospite aggiornato"); }
      else { await api.post("/guests", form); toast.success("Ospite aggiunto"); }
      closeForm(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo profilo ospite?")) return;
    await api.delete(`/guests/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id)); setDetail(null);
    toast.success("Ospite eliminato");
  };

  const filtered = useMemo(() => {
    let list = items;
    if (tab === "yacht") list = list.filter((g) => (g.charter_ids || []).length > 0);
    if (tab === "today") list = list.filter((g) => (g.charter_ids || []).some((id) => activeCharterIds.includes(id)));
    const showArchived = chips.includes("archived");
    list = list.filter((g) => (showArchived ? true : !g.archived));
    CHIPS.forEach((c) => { if (c.key !== "archived" && chips.includes(c.key)) list = list.filter((g) => g[c.key]); });
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((g) => `${g.name} ${g.code || ""}`.toLowerCase().includes(s));
    return list;
  }, [items, tab, chips, q, activeCharterIds]);

  const TABS = [
    { key: "global", label: "Directory globale" },
    { key: "yacht", label: "Su questo yacht" },
    { key: "today", label: "A bordo oggi" },
  ];

  const notePreview = (g) => (g.notes || g.preferences || "").split("\n").slice(0, 3).join(" · ");

  return (
    <div className="space-y-6" data-testid="ospiti-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold text-navy">Ospiti</h1>
          <p className="mt-1 text-sm text-slate-500">Anagrafica ospiti dello yacht, con allergie e preferenze</p>
        </div>
        {canWrite && (
          <button data-testid="guest-new-btn" onClick={openNew} className="flex h-11 items-center gap-2 rounded-lg bg-navy px-4 font-semibold text-white transition-colors hover:bg-navy-hover">
            <Plus className="h-5 w-5" /> Nuovo ospite
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`ospiti-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-navy text-white" : "bg-white text-navy hover:bg-slate-100"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input data-testid="ospiti-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, cognome o codice…" className="h-11 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
        </div>
        <div className="flex flex-wrap gap-2">
          {CHIPS.map((c) => (
            <button key={c.key} data-testid={`ospiti-chip-${c.key}`} onClick={() => toggleChip(c.key)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${chips.includes(c.key) ? "border-gold bg-gold/15 text-navy" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="ospiti-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-navy/5 text-navy/40"><Users className="h-8 w-8" /></div>
          <p className="mt-4 font-head text-lg font-semibold text-navy">Nessun ospite registrato</p>
          <p className="mt-1 text-sm text-slate-500">Aggiungine uno per iniziare a proteggere il tuo menù.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((g) => (
            <div key={g.id} data-testid={`guest-card-${g.id}`} onClick={() => setDetail(g)}
              className="group relative cursor-pointer rounded-2xl border border-navy/10 bg-white p-5 shadow-sm transition-transform hover:-translate-y-1">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-head text-sm font-bold text-white" style={{ background: colorFor(g.id) }}>{g.photo_url ? <img src={g.photo_url} alt="" className="h-12 w-12 rounded-full object-cover" /> : initials(g)}</div>
                <div className="min-w-0">
                  <div className="truncate font-head text-base font-semibold text-navy">{g.name}{g.archived ? " (arch.)" : ""}</div>
                  <div className="text-xs text-slate-400">{g.code || ""}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-base">
                {g.severe_allergy && <span title="Allergia grave">🥜</span>}
                {g.vegan && <span title="Vegano">🌱</span>}
                {g.gluten_free && <span title="Gluten-free">🌾</span>}
                {g.vip && <span title="VIP">⭐</span>}
                {(g.aversions || "").trim() && <span title="Avversioni">🚫</span>}
                {(g.allergen_ids || []).length > 0 && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">{g.allergen_ids.length} allergeni</span>}
              </div>
              {notePreview(g) && (
                <div className="pointer-events-none absolute inset-x-3 bottom-3 rounded-lg bg-navy px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                  {notePreview(g).slice(0, 120)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* DETAIL */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDetail(null)}>
          <div data-testid="guest-detail" className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-full font-head font-bold text-white" style={{ background: colorFor(detail.id) }}>{detail.photo_url ? <img src={detail.photo_url} alt="" className="h-14 w-14 rounded-full object-cover" /> : initials(detail)}</div>
                <div>
                  <h2 className="font-head text-xl font-bold text-navy">{detail.name}</h2>
                  <div className="text-xs text-slate-400">{detail.code} {detail.diet ? `· ${detail.diet}` : ""}</div>
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-navy"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-base">
              {detail.severe_allergy && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">🥜 Allergia grave</span>}
              {detail.vegan && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">🌱 Vegano</span>}
              {detail.gluten_free && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">🌾 Gluten-free</span>}
              {detail.vip && <span className="rounded-full bg-gold/15 px-2 py-0.5 text-xs text-navy">⭐ VIP</span>}
              {detail.child && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Bambino</span>}
            </div>
            {(detail.allergen_ids || []).length > 0 && (
              <div className="mt-4"><div className="text-xs font-semibold uppercase text-red-600">Allergie</div>
                <div className="mt-1 flex flex-wrap gap-1.5">{detail.allergen_ids.map((id) => <span key={id} className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">{allergenName(id)}</span>)}</div>
              </div>
            )}
            {detail.intolerances && <p className="mt-3 text-sm text-slate-600"><b className="text-navy">Intolleranze:</b> {detail.intolerances}</p>}
            {detail.aversions && <p className="mt-1 text-sm text-slate-600"><b className="text-navy">Avversioni:</b> {detail.aversions}</p>}
            {detail.preferences && <p className="mt-1 text-sm text-slate-600"><b className="text-navy">Preferenze:</b> {detail.preferences}</p>}
            {detail.notes && <p className="mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-500">{detail.notes}</p>}
            {canWrite && (
              <div className="mt-5 flex gap-2">
                <button data-testid="guest-detail-edit" onClick={() => openEdit(detail)} className="flex h-11 items-center gap-2 rounded-lg bg-navy px-4 font-semibold text-white hover:bg-navy-hover"><Pencil className="h-4 w-4" /> Modifica</button>
                <button data-testid="guest-detail-delete" onClick={() => remove(detail.id)} className="flex h-11 items-center gap-2 rounded-lg border-2 border-red-200 px-4 font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /> Elimina</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FORM */}
      {showForm && canWrite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeForm}>
          <form onSubmit={save} data-testid="guest-form" className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-head text-lg font-semibold text-navy">{editingId ? "Modifica ospite" : "Nuovo ospite"}</h2>
              <button type="button" onClick={closeForm} className="text-slate-400 hover:text-navy"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Nome *</label>
                <input data-testid="guest-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 outline-none focus:border-gold" /></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Cognome</label>
                <input data-testid="guest-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 outline-none focus:border-gold" /></div>
            </div>
            <div className="mt-4 flex items-center gap-4">
              {form.photo_url ? <img src={form.photo_url} alt="" className="h-16 w-16 rounded-full object-cover" /> : <div className="flex h-16 w-16 items-center justify-center rounded-full bg-navy text-sm font-bold text-gold">{((form.first_name?.[0] || "") + (form.last_name?.[0] || "")).toUpperCase() || "?"}</div>}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Foto profilo</label>
                <input data-testid="guest-photo" type="file" accept="image/*" onChange={(e) => handlePhoto(e.target.files[0])} className="text-sm text-slate-500" />
                {form.photo_url && <button type="button" onClick={() => set("photo_url", "")} className="ml-2 text-xs font-semibold text-red-500">Rimuovi</button>}
              </div>
            </div>
            <div className="mt-4"><label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Regime alimentare</label>
              <select data-testid="guest-diet" value={form.diet} onChange={(e) => set("diet", e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold">
                <option value="">— Nessuno —</option>{DIETS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select></div>
            <div className="mt-4"><label className="mb-2 block text-xs font-semibold uppercase text-slate-400">Allergie (14 allergeni UE)</label>
              <div className="flex flex-wrap gap-2">{ALLERGENS.map((a) => { const on = form.allergen_ids.includes(a.id); return (
                <button type="button" key={a.id} data-testid={`guest-allergen-${a.id}`} onClick={() => toggleArr("allergen_ids", a.id)} className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${on ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>{a.name}</button>);
              })}</div></div>
            <div className="mt-4 flex flex-wrap gap-4">
              {[["severe_allergy", "Allergia grave"], ["vegan", "Vegano"], ["gluten_free", "Gluten-free"], ["vip", "VIP"], ["child", "Bambino"], ["archived", "Archiviato"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" data-testid={`guest-${k}`} checked={form[k]} onChange={() => toggle(k)} className="h-4 w-4 accent-gold" /> {l}</label>
              ))}
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Intolleranze</label>
                <input data-testid="guest-intolerances" value={form.intolerances} onChange={(e) => set("intolerances", e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 outline-none focus:border-gold" /></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Avversioni</label>
                <input data-testid="guest-aversions" value={form.aversions} onChange={(e) => set("aversions", e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 outline-none focus:border-gold" /></div>
            </div>
            <div className="mt-4"><label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Preferenze</label>
              <input data-testid="guest-preferences" value={form.preferences} onChange={(e) => set("preferences", e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 outline-none focus:border-gold" /></div>
            {charters.length > 0 && (
              <div className="mt-4"><label className="mb-2 block text-xs font-semibold uppercase text-slate-400">Collega ai charter</label>
                <div className="flex flex-wrap gap-2">{charters.map((c) => { const on = form.charter_ids.includes(c.id); return (
                  <button type="button" key={c.id} onClick={() => toggleArr("charter_ids", c.id)} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>{c.name}</button>);
                })}</div></div>
            )}
            <div className="mt-4"><label className="mb-1 block text-xs font-semibold uppercase text-slate-400">Note</label>
              <textarea data-testid="guest-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="w-full rounded-lg border-2 border-slate-200 p-3 outline-none focus:border-gold" /></div>
            <div className="mt-5 flex gap-2">
              <button data-testid="guest-save" type="submit" disabled={saving} className="flex h-12 items-center gap-2 rounded-lg bg-navy px-6 font-bold text-white hover:bg-navy-hover disabled:opacity-60">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} {editingId ? "Salva modifiche" : "Aggiungi ospite"}
              </button>
              <button type="button" onClick={closeForm} className="h-12 rounded-lg border-2 border-slate-200 px-6 font-semibold text-navy hover:bg-slate-50">Annulla</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
